import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, MapPin, Star } from "lucide-react";
import { api } from "@/lib/api";
import { VenueCard, EventCard } from "@/components/asv/Cards";
import { CentreCard } from "@/components/asv/CentreCard";
import { Rail, RailItem } from "@/components/asv/Rail";
import { ShareButton, shareText } from "@/components/asv/ShareButton";
import { Gallery, InfoCard, FacilitiesCard, Memberships } from "@/components/asv/CentreParts";

const Section = ({ title, children, testid }) => (
  <section className="mt-12" data-testid={testid}><h2 className="asv-display mb-5 text-2xl font-extrabold">{title}</h2>{children}</section>
);

export default function AsvCentre() {
  const { slug } = useParams();
  const [c, setC] = useState(undefined);
  useEffect(() => { setC(undefined); window.scrollTo(0, 0); api.get(`/asv/centres/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [slug]);
  if (c === undefined) return <Loader2 className="mx-auto mt-24 h-8 w-8 animate-spin asv-accent" />;
  if (c === null) return <div className="pt-16 text-center" data-testid="centre-not-found"><p className="asv-display text-2xl font-extrabold">Sport centre not found</p><Link to="/sport-centres" className="asv-btn mt-6 inline-flex">See all sport centres</Link></div>;
  return (
    <div className="pt-8" data-testid="asv-centre">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {c.logo && <img src={c.logo} alt="" className="h-14 w-14 shrink-0 rounded-2xl bg-white object-contain p-1" data-testid="centre-logo" />}
          <div className="min-w-0"><h1 className="asv-display text-3xl font-extrabold sm:text-4xl" data-testid="centre-name">{c.name}</h1>
            <p className="asv-muted mt-1 flex items-start gap-1 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{c.address}</p>
            {!!c.rating_count && <p className="mt-1 inline-flex items-center gap-1 text-sm font-bold"><Star className="h-4 w-4 fill-current asv-accent" />{c.rating.toFixed(1)} <span className="asv-muted font-normal">({c.rating_count} reviews)</span></p>}
          </div>
        </div>
        <ShareButton kind="centre" id={c.slug} path={`/sport-centres/${c.slug}`} text={shareText.centre(c)} />
      </div>
      <Gallery photos={c.photos} name={c.name} />
      {(c.short_description || c.about?.text) && <p className="asv-muted mt-6 text-sm leading-relaxed sm:text-base" data-testid="centre-about">{c.about?.text || c.short_description}</p>}
      <div className="mt-6 grid gap-5 lg:grid-cols-2"><InfoCard c={c} /><FacilitiesCard c={c} /></div>
      <div className="mt-8"><Memberships c={c} /></div>
      {!!c.venue_list.length && <Section title="Venues" testid="centre-venues"><Rail testid="rail-centre-venues">{c.venue_list.map((x) => <RailItem key={x.key}><VenueCard x={x} /></RailItem>)}</Rail></Section>}
      {!!c.event_list.length && <Section title="Upcoming events" testid="centre-events"><Rail testid="rail-centre-events">{c.event_list.map((x) => <RailItem key={x.key}><EventCard x={x} /></RailItem>)}</Rail></Section>}
      {!!c.nearby.length && <Section title="Sport Centre Nearby" testid="centre-nearby"><Rail testid="rail-centre-nearby">{c.nearby.map((o) => <RailItem key={o.slug}><CentreCard c={o} /></RailItem>)}</Rail></Section>}
    </div>
  );
}
